import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { LayoutDashboard, Check, BarChart3, PieChart, TrendingUp } from 'lucide-react';

const DASHBOARD_TYPES = [
  { id: 'revenue', label: 'Revenue-Focused', description: 'Track income, margins, and growth trends', icon: TrendingUp },
  { id: 'operations', label: 'Operations-Focused', description: 'Monitor efficiency, delivery rates, and throughput', icon: BarChart3 },
  { id: 'performance', label: 'Performance Summary', description: 'High-level KPIs and team performance overview', icon: PieChart },
];

const METRICS_OPTIONS = [
  { id: 'revenue', label: 'Revenue & Sales' },
  { id: 'conversion', label: 'Conversion Rates' },
  { id: 'customer_acquisition', label: 'Customer Acquisition Cost' },
  { id: 'retention', label: 'Retention & Churn' },
  { id: 'profit_margins', label: 'Profit Margins' },
  { id: 'lead_pipeline', label: 'Lead Pipeline' },
  { id: 'delivery_rates', label: 'Delivery / Fulfillment Rates' },
  { id: 'growth_rate', label: 'Growth Rate' },
];

const VISUAL_FORMATS = [
  { id: 'charts', label: 'Charts & Graphs', description: 'Bar, line, and area charts' },
  { id: 'tables', label: 'Data Tables', description: 'Sortable, filterable tables' },
  { id: 'kpi_cards', label: 'KPI Cards', description: 'Big number summaries with trends' },
  { id: 'mixed', label: 'Mixed (Recommended)', description: 'Combination of all formats' },
];

interface DashboardConfig {
  type?: string;
  metrics?: string[];
  visual_format?: string;
}

interface Props {
  config: DashboardConfig;
  onUpdate: (config: DashboardConfig) => void;
}

export const DashboardPrefsStep = ({ config, onUpdate }: Props) => {
  const selectedType = config.type || '';
  const selectedMetrics = config.metrics || [];
  const selectedFormat = config.visual_format || '';

  const toggleMetric = (id: string) => {
    const updated = selectedMetrics.includes(id)
      ? selectedMetrics.filter(m => m !== id)
      : [...selectedMetrics, id];
    onUpdate({ ...config, metrics: updated });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <LayoutDashboard className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-lg">Dashboard Configuration</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Choose how your insights will be displayed. Dashboards are generated automatically after each workflow run.
        </p>
      </div>

      {/* Dashboard type */}
      <div>
        <h4 className="text-sm font-medium mb-2">Dashboard Type</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {DASHBOARD_TYPES.map((dt) => (
            <Card
              key={dt.id}
              className={`p-4 cursor-pointer transition-all hover:border-primary/50 ${
                selectedType === dt.id ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : ''
              }`}
              onClick={() => onUpdate({ ...config, type: dt.id })}
            >
              <div className="flex items-center gap-2 mb-1">
                <dt.icon className="h-4 w-4 text-primary" />
                <p className="font-medium text-sm">{dt.label}</p>
                {selectedType === dt.id && <Check className="h-3.5 w-3.5 text-primary ml-auto" />}
              </div>
              <p className="text-xs text-muted-foreground">{dt.description}</p>
            </Card>
          ))}
        </div>
      </div>

      {/* Metrics */}
      <div>
        <h4 className="text-sm font-medium mb-2">
          Metrics to Display
          {selectedMetrics.length > 0 && (
            <Badge variant="secondary" className="ml-2">{selectedMetrics.length} selected</Badge>
          )}
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {METRICS_OPTIONS.map((m) => (
            <div
              key={m.id}
              className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all ${
                selectedMetrics.includes(m.id) ? 'border-primary bg-primary/5' : 'hover:border-primary/30'
              }`}
              onClick={() => toggleMetric(m.id)}
            >
              <Checkbox checked={selectedMetrics.includes(m.id)} onCheckedChange={() => toggleMetric(m.id)} />
              <Label className="text-xs cursor-pointer">{m.label}</Label>
            </div>
          ))}
        </div>
      </div>

      {/* Visual format */}
      <div>
        <h4 className="text-sm font-medium mb-2">Preferred Visual Format</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {VISUAL_FORMATS.map((vf) => (
            <Card
              key={vf.id}
              className={`p-3 cursor-pointer transition-all hover:border-primary/50 ${
                selectedFormat === vf.id ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : ''
              }`}
              onClick={() => onUpdate({ ...config, visual_format: vf.id })}
            >
              <p className="font-medium text-xs">{vf.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{vf.description}</p>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
